import { applyLiveValuesTransform } from '@framework/validation/live/applyLiveValuesTransform.ts';
import { type ILiveValuesTransformEvent } from '@framework/validation/live/LiveTypes.ts';
import { get, type UseFormReturn } from 'react-hook-form';
import { type AnyObject, type ObjectSchema } from 'yup';
import { castValuesWithLivePaths } from '../form/castValuesWithLivePaths.ts';
import { isPlainObject, isSerializableLeaf } from '../utils/isPlainObject.ts';
import { setByPath, unsetByPath } from '../utils/pathAccessors.ts';

// NOSONAR
// /** Attivare per loggare tutte le modifiche applicate da syncComputedValues */
// const DEBUG_SYNC_VALUES = process.env.NODE_ENV === 'development';
//
// function debugLog(path: string, from: unknown, to: unknown) {
//   if (DEBUG_SYNC_VALUES) {
//     console.debug('[syncComputed] SET', path, from, '→', to);
//   }
// }

/**
 * Sincronizza il form state con il risultato di `schema.cast()`.
 *
 * Tre fasi:
 * 1. **Diff & write** — confronta cast vs current, scrive le differenze
 *    direttamente in `control._formValues` (bypassa `setValue` di RHF
 *    per evitare N × cloneObject + broadcast)
 * 2. **Dirty tracking** — aggiorna `dirtyFields` confrontando ogni campo
 *    modificato con `initialValues`
 * 3. **Broadcast** — emette notifiche mirate per-campo su `_subjects.state`
 *    e `_subjects.array` (solo i componenti dei campi cambiati si aggiornano)
 */
export function syncComputedValues(
  methods: UseFormReturn<any>,
  schema: ObjectSchema<AnyObject>,
  validationContext: any,
  initialValues?: any,
  events?: ILiveValuesTransformEvent[],
) {
  const values = methods.getValues();
  const control = methods.control;

  const { values: transformedValues, changedPaths: transformedChangedPaths } =
    applyLiveValuesTransformEvents(values, schema, validationContext, events);

  const cast = castValuesWithLivePaths(
    schema,
    transformedValues,
    validationContext,
  );

  if (!cast || typeof cast !== 'object') return;

  // --- Fase 1: Diff & write ---
  // Scrive i valori della transform, poi confronta cast vs transformed e
  // scrive i computed aggiuntivi. Raccoglie i path modificati da entrambi.
  const changedPaths: string[] = [];
  reconcileDifferences(
    control._formValues,
    transformedValues,
    cast,
    '',
    changedPaths,
    schema.fields,
    transformedChangedPaths,
    validationContext,
  );

  // NOSONAR: log modifiche disattivato per performance
  // if (process.env.NODE_ENV === 'development' && changedPaths.length > 0) {
  //   console.debug('[syncComputedValues]', changedPaths.length, 'campi modificati:', changedPaths);
  // }

  if (changedPaths.length === 0) return;

  // --- Fase 1b: Refresh riferimenti ancestor ---
  // setByPath muta l'oggetto foglia in-place, ma gli oggetti ancestor
  // mantengono lo stesso riferimento. useWatch (RHF) internamente usa
  // useState, che si refresha su `Object.is(prev, next) === true`.
  // Esempio: se cambia `a.b.c`, sia `a.b` che `a` restano lo stesso
  // oggetto → un watcher su `a` non si aggiorna.
  // Cloniamo (shallow clone) tutti gli ancestor così ogni livello ha
  // un nuovo riferimento.
  refreshAncestorReferences(control._formValues, changedPaths);

  // --- Fase 2: Dirty tracking ---
  // RHF non traccia dirty automaticamente perché bypassiamo setValue.
  // Confrontiamo con initialValues (stabili) e non con control._defaultValues
  // perché RHF cancella _defaultValues durante l'unregister dei campi stripped.
  const defaults = initialValues ?? control._defaultValues;
  const { dirtyFields } = control._formState;

  // biome-ignore lint/style/useForOf: performance-critical hot path
  for (let i = 0; i < changedPaths.length; i++) /* NOSONAR */ {
    const path = changedPaths[i];
    const newValue = get(control._formValues, path);
    const defaultValue = get(defaults, path);

    if (areSameLeaf(newValue, defaultValue)) {
      unsetByPath(dirtyFields, path);
    } else {
      setByPath(dirtyFields, path, true);
    }
  }

  // --- Fase 3: Broadcast ---
  const valuesSnapshot = { ...control._formValues };

  // 3a. Notifica useFieldArray per i repeatable che contengono campi modificati.
  // useFieldArray sottoscrive a _subjects.array e aggiorna il suo stato
  // interno (fields, ids) quando riceve una notifica con il suo name.
  notifyFieldArrays(control, changedPaths, valuesSnapshot);

  // 3b. Broadcast mirati per-campo su _subjects.state.
  // Includono `name` nel payload così `shouldSubscribeByName` filtra e
  // solo i subscriber del campo specifico ricevono il re-render.
  // Includono `dirtyFields` per aggiornare i consumer come FormPreventNavigation.
  const stateNext = control._subjects.state.next;
  // biome-ignore lint/style/useForOf: performance-critical hot path
  for (let i = 0; i < changedPaths.length; i++) /* NOSONAR */ {
    stateNext({
      name: changedPaths[i],
      values: valuesSnapshot,
      dirtyFields,
    });
  }
}

/**
 * Applica la values transform per ogni evento del tick, accumulando i
 * valori riscritti e l'unione dei path modificati.
 *
 * La transform lavora su un singolo evento `{ path, value }`: con più campi
 * modificati nello stesso tick (paste, autofill, update programmatici) va
 * invocata una volta per evento, altrimenti solo l'ultimo cambiamento
 * verrebbe propagato.
 *
 * Gli eventi sono processati dal più recente al più vecchio: se la
 * transform di un evento riscrive il path di un evento più vecchio (campi
 * che si sincronizzano a vicenda, es. stesso semantic tag univoco),
 * quell'evento è obsoleto e viene saltato. Così vince l'ultima modifica
 * sull'intero gruppo, senza scritture incrociate.
 *
 * Senza eventi viene comunque invocata una volta (event undefined) per
 * preservare eventuali transform non guidate dall'evento.
 */
function applyLiveValuesTransformEvents(
  values: any,
  schema: ObjectSchema<AnyObject>,
  validationContext: any,
  events?: ILiveValuesTransformEvent[],
): { values: any; changedPaths: string[] } {
  const eventsToApply: (ILiveValuesTransformEvent | undefined)[] =
    events && events.length > 0 ? events : [undefined];

  let transformedValues = values;
  const changedPaths: string[] = [];
  for (let i = eventsToApply.length - 1; i >= 0; i--) {
    const event = eventsToApply[i];

    // Evento obsoleto: il suo path è già stato riscritto dalla transform
    // di un evento più recente, il valore utente che portava non vale più.
    if (event?.path && changedPaths.includes(event.path)) continue;

    const result = applyLiveValuesTransform(schema, {
      values: transformedValues,
      context: validationContext,
      event,
    });
    transformedValues = result.values;
    for (const changedPath of result.changedPaths ?? []) {
      if (!changedPaths.includes(changedPath)) {
        changedPaths.push(changedPath);
      }
    }
  }

  return { values: transformedValues, changedPaths };
}

/**
 * Notifica `_subjects.array` solo quando l'array stesso è stato
 * sovrascritto (cambio lunghezza → path esatto = nome dell'array).
 *
 * NON notifica per sotto-path (es. `lista.0.nome`): quei cambiamenti
 * sono già propagati dai broadcast per-campo su `_subjects.state`.
 * Notificare `_subjects.array` per sotto-path corrompe `useFieldArray`
 * di RHF, che nel subscriber rigenera tutti gli id
 * (`ids.current = fieldValues.map(generateId)`), sovrascrivendo
 * quelli stabili mantenuti da `append`/`remove`.
 */
function notifyFieldArrays(
  control: UseFormReturn<any>['control'],
  changedPaths: string[],
  valuesSnapshot: Record<string, any>,
) {
  const registeredArrays = control._names.array;
  if (registeredArrays.size === 0) return;

  const alreadyNotified = new Set<string>();

  // biome-ignore lint/style/useForOf: performance-critical hot path
  for (let i = 0; i < changedPaths.length; i++) /* NOSONAR */ {
    const path = changedPaths[i];
    for (const arrayName of registeredArrays) {
      if (path === arrayName && !alreadyNotified.has(arrayName)) {
        alreadyNotified.add(arrayName);
        control._subjects.array.next({
          name: arrayName,
          values: valuesSnapshot,
        });
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Riconciliazione ricorsiva: confronta current vs cast e scrive le differenze
// ---------------------------------------------------------------------------

/**
 * Confronta ricorsivamente `current` e `cast`, scrivendo direttamente
 * in `formValues` (`control._formValues`) i campi che differiscono.
 *
 * Al livello top (prefix=''), scrive prima tutti i path modificati dalla
 * transform, poi procede col confronto cast vs current per i computed.
 *
 * Rimuove i campi presenti in `current` ma assenti in `cast` SOLO se
 * sono definiti nello schema Yup (campi stripped via `.strip()`).
 * I campi sconosciuti allo schema (es. `__internal_*`) non vengono
 * toccati, anche se `.noUnknown()` li esclude dal cast.
 */
// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: riconciliazione ricorsiva di due alberi di valori
// biome-ignore lint/complexity/useMaxParams: funzione ricorsiva interna, l'accumulator `changedPaths` deve essere passato
function reconcileDifferences /* NOSONAR */(
  formValues: Record<string, any>,
  current: any,
  cast: any,
  prefix: string,
  changedPaths: string[],
  schemaFields?: Record<string, any>,
  transformedChangedPaths: string[] = [],
  validationContext?: any,
) {
  if (cast == null || typeof cast !== 'object') return;

  // Al livello top-level, scrivi i path prodotti dalla transform
  if (prefix === '') {
    const existing = new Set(changedPaths);
    for (const transformPath of transformedChangedPaths) {
      if (existing.has(transformPath)) continue;
      const transformedValue = get(current, transformPath);
      setByPath(formValues, transformPath, transformedValue);
      existing.add(transformPath);
      changedPaths.push(transformPath);
    }
  }

  // Campi presenti nel cast: aggiorna se diversi dal current
  const castKeys = Object.keys(cast);
  // biome-ignore lint/style/useForOf: performance-critical hot path
  for (let i = 0; i < castKeys.length; i++) /* NOSONAR */ {
    const key = castKeys[i];
    const castValue = cast[key];
    const currentValue = current?.[key];

    if (areSameLeaf(castValue, currentValue)) continue;

    const path = prefix ? `${prefix}.${key}` : key;

    if (Array.isArray(castValue)) {
      const arrayInnerFields = schemaFields?.[key]?.innerType?.fields;
      reconcileArray(
        formValues,
        currentValue,
        castValue,
        path,
        changedPaths,
        arrayInnerFields,
        validationContext,
      );
      // biome-ignore lint/style/noNegationElse: Teniamo il caso base alla fine
    } else if (!isSerializableLeaf(castValue)) {
      let fieldSchema = schemaFields?.[key];
      // Schema lazy (es. WidgetDirectivesSchema): va risolto per poter
      // scendere nei campi e produrre scritture/broadcast per-foglia.
      // Senza resolve l'oggetto verrebbe sovrascritto in blocco con un
      // unico broadcast sul path padre, che i subscriber exact
      // (useController di RHF) non ricevono.
      if (fieldSchema?.type === 'lazy') {
        fieldSchema = fieldSchema.resolve({
          value: castValue,
          parent: cast,
          context: validationContext,
        });
      }
      if (fieldSchema?.fields || (!fieldSchema && isPlainObject(castValue))) {
        reconcileDifferences(
          formValues,
          currentValue ?? {},
          castValue,
          path,
          changedPaths,
          fieldSchema?.fields,
          [],
          validationContext,
        );
      } else {
        setByPath(formValues, path, castValue);
        changedPaths.push(path);
      }
    } else {
      // NOSONAR
      // debugLog(path, currentValue, castValue);
      setByPath(formValues, path, castValue);
      changedPaths.push(path);
    }
  }

  // Campi stripped: presenti nel form ma assenti nel cast.
  // schema.cast() rimuove i campi con .strip() attivo; allineiamo il form
  // state perché le condizioni a cascata (when/parentWhen) vedano i valori
  // corretti al prossimo resolvedStore.resolve.
  if (
    current != null &&
    typeof current === 'object' &&
    !Array.isArray(current)
  ) {
    const currentKeys = Object.keys(current);
    // biome-ignore lint/style/useForOf: performance-critical hot path
    for (let i = 0; i < currentKeys.length; i++) /* NOSONAR */ {
      const key = currentKeys[i];
      if (key in cast) continue;
      if (current[key] === undefined) continue; // già rimosso, skip

      // Rimuoviamo SOLO campi definiti nello schema: i campi sconosciuti
      // (es. __internal_*) vengono esclusi dal cast da `.noUnknown()` ma
      // non devono essere rimossi dal form state.
      if (schemaFields && !(key in schemaFields)) continue;

      const path = prefix ? `${prefix}.${key}` : key;
      // NOSONAR
      // debugLog(`${path} (STRIPPED)`, get(formValues, path), undefined);
      unsetByPath(formValues, path);
      changedPaths.push(path);
    }
  }
}

/**
 * Riconciliazione per array: se le lunghezze differiscono, sovrascrive
 * l'intero array. Altrimenti confronta elemento per elemento.
 */
// biome-ignore lint/complexity/useMaxParams: funzione ricorsiva interna, l'accumulator `changedPaths` deve essere passato
function reconcileArray(
  formValues: Record<string, any>,
  currentArray: any,
  castArray: any[],
  path: string,
  changedPaths: string[],
  schemaFields?: Record<string, any>,
  validationContext?: any,
) {
  // Lunghezze diverse o current non è un array: sovrascriviamo tutto
  if (
    !Array.isArray(currentArray) ||
    currentArray.length !== castArray.length
  ) {
    // NOSONAR
    // debugLog(`${path} (ARRAY OVERWRITE)`, currentArray, castArray);
    setByPath(formValues, path, castArray);
    changedPaths.push(path);
    return;
  }

  // Stessa lunghezza: confronto elemento per elemento
  for (let i = 0; i < castArray.length; i++) {
    const castItem = castArray[i];
    const currentItem = currentArray[i];

    if (areSameLeaf(castItem, currentItem)) continue;

    // Dot notation per indici array: RHF riconosce i campi dentro
    // useFieldArray tramite il pattern `array.0.field`.
    const itemPath = `${path}.${i}`;

    if (Array.isArray(castItem)) {
      reconcileArray(
        formValues,
        currentItem,
        castItem,
        itemPath,
        changedPaths,
        undefined,
        validationContext,
      );
    } else if (
      !isSerializableLeaf(castItem) &&
      (schemaFields || isPlainObject(castItem))
    ) {
      reconcileDifferences(
        formValues,
        currentItem ?? {},
        castItem,
        itemPath,
        changedPaths,
        schemaFields,
        [],
        validationContext,
      );
    } else {
      setByPath(formValues, itemPath, castItem);
      changedPaths.push(itemPath);
    }
  }
}

/**
 * Crea nuovi riferimenti (shallow clone) per tutti gli oggetti ancestor
 * dei path modificati, dalla foglia più profonda alla root.
 *
 * Necessario perché setByPath muta la foglia in-place senza toccare i
 * nodi intermedi. useWatch (RHF) usa useState che si refresha su
 * `Object.is(prev, next) === true`: senza il refresh, un watcher su
 * `a` non vedrebbe il cambiamento di `a.b.c`.
 */
function refreshAncestorReferences(
  formValues: Record<string, any>,
  changedPaths: string[],
) {
  const ancestors = new Set<string>();

  for (const path of changedPaths) {
    let current = path;
    while (true) {
      const lastDot = current.lastIndexOf('.');
      if (lastDot <= 0) break;
      current = current.substring(0, lastDot);
      ancestors.add(current);
    }
  }

  const deepestFirst = [...ancestors].sort((a, b) => b.length - a.length);

  for (const path of deepestFirst) {
    const value = get(formValues, path);
    if (value != null && typeof value === 'object') {
      setByPath(
        formValues,
        path,
        Array.isArray(value) ? [...value] : { ...value },
      );
    }
  }
}

/**
 * Uguaglianza stretta tra due valori foglia.
 */
function areSameLeaf(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a instanceof Date && b instanceof Date)
    return a.getTime() === b.getTime();
  return false;
}
